import {login} from '@/api';
import FormControls from '@/components/FormControls.tsx';
import ForgotPasswordDialog from '@/pages/auth/ForgotPasswordDialog.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, FormGroup, H1, InputGroup} from '@blueprintjs/core';
import {ChangeEvent, ReactElement, SyntheticEvent, useCallback, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router';

export default function Login(): ReactElement {
	const [searchParams, _setSearchParams] = useSearchParams();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);

	const [showResetDialog, setShowResetDialog] = useState(false);

	const onPasswordResetClick = useCallback(() => setShowResetDialog(true), []);
	const onResetDialogClose = useCallback(() => setShowResetDialog(false), []);

	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');

	const onUsernameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		setUsername(event.currentTarget.value);
	}, []);

	const onPasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		setPassword(event.currentTarget.value);
	}, []);

	const submit = useCallback(async (event: SyntheticEvent) => {
		event.preventDefault();

		if (loading)
			return;

		setLoading(true);

		try {
			await login(username, password);
		} catch (e) {
			setLoading(false);
			console.error(e);

			ToastErrors.unknown();

			return;
		}

		Toaster.success('Welcome back!');
		navigate(searchParams.get('redirect') ?? '/feeds');
	}, [username, password, loading]);

	return (
		<>
			<H1 style={{marginBottom: 25}}>Log In</H1>

			<form onSubmit={submit}>
				<FormGroup label="Email Address" labelFor="emailAddress">
					<InputGroup name="emailAddress" value={username} onChange={onUsernameChange} autoFocus={true} />
				</FormGroup>

				<FormGroup label="Password" labelFor="password">
					<InputGroup name="password" type="password" value={password} onChange={onPasswordChange} />
				</FormGroup>

				<FormControls>
					<Button text="Forgot Password" disabled={loading} onClick={onPasswordResetClick} />
					<Button type="submit" intent="primary" text="Login" onClick={submit} loading={loading} />
				</FormControls>
			</form>

			{showResetDialog && <ForgotPasswordDialog onClose={onResetDialogClose} />}
		</>
	);
}
